import React, { createContext, useContext } from 'react';

// Single source of truth for which languages the webshop admin works with.
//
// Two notions, backed by two webshop settings:
//   - ADMIN_LANGUAGES    → which languages are EDITABLE in the admin (the
//                          per-language tabs/fields shown across the config UI).
//   - VISIBILE_LANGUAGES → which languages are ACTIVE on the storefront (the
//                          language bar / what customers see). Already existed
//                          on the backend; now editable. Always a subset of the
//                          admin-available set (you can't show what you can't fill).
//
// All per-language renderers should read `useAdminLanguages()` instead of a
// hardcoded NL/FR/EN list, so hiding a language hides its fields everywhere.

export const ALL_LANGUAGES = [
    { code: 'NL', short: 'NL', label: 'Nederlands' },
    { code: 'FR', short: 'FR', label: 'Français' },
    { code: 'EN', short: 'EN', label: 'English' },
];
export const ALL_LANGUAGE_CODES = ALL_LANGUAGES.map(l => l.code);

// Parse a backend CSV ("nl,fr,en") into uppercase codes in canonical order
// (['NL','FR','EN']), keeping only known languages. Returns null when empty so
// callers can fall back to a default.
export const parseLangCsv = (csv) => {
    if (!csv || typeof csv !== 'string') return null;
    const set = new Set(csv.split(',').map(c => c.trim().toUpperCase()).filter(Boolean));
    const out = ALL_LANGUAGE_CODES.filter(c => set.has(c));
    return out.length ? out : null;
};

// Serialise uppercase codes back to the lowercase CSV the backend stores.
export const toLangCsv = (codes) =>
    ALL_LANGUAGE_CODES.filter(c => (codes || []).includes(c)).map(c => c.toLowerCase()).join(',');

// Keep an arbitrary code array in canonical NL/FR/EN order, de-duped.
export const orderLangs = (codes) =>
    ALL_LANGUAGE_CODES.filter(c => (codes || []).includes(c));

const LanguagesContext = createContext({
    adminLanguages:   ALL_LANGUAGE_CODES,
    visibleLanguages: ALL_LANGUAGE_CODES,
});

export const LanguagesProvider = LanguagesContext.Provider;

// Raw uppercase code arrays { adminLanguages, visibleLanguages }.
export const useWebshopLanguages = () => useContext(LanguagesContext);

// The languages editable in the admin, as [{code,short,label}] in canonical
// order. Falls back to all languages when nothing is configured yet.
export const useAdminLanguages = () => {
    const { adminLanguages } = useContext(LanguagesContext);
    const codes = (adminLanguages && adminLanguages.length) ? adminLanguages : ALL_LANGUAGE_CODES;
    return ALL_LANGUAGES.filter(l => codes.includes(l.code));
};
